export const PORTRAIT_ROWS_PER_PAGE = 31;
export const LANDSCAPE_DETAIL_ROWS_PER_PAGE = 23;
export const LANDSCAPE_BREAKDOWN_ROWS_PER_PAGE = 15;

export const isEnglishEstimateLanguage = languageType => languageType === 'english';

export const visibleRecords = records =>
  (records || []).filter(record => !record?._destroy);

export const formatEstimateTaxLabel = value => {
  const taxRate = Number(value);
  if (!Number.isFinite(taxRate) || taxRate === 0) return '消費税';

  return `消費税（${taxRate.toLocaleString('ja-JP', { maximumFractionDigits: 2 })}%）`;
};

export const hasAtMostDecimalPlaces = (value, maximum) => {
  if (value === '' || value == null) return true;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return false;

  return Number(numericValue.toFixed(maximum)) === numericValue;
};

export const normalizeDecimalInputValue = value => {
  if (value === '' || value == null) return '';

  const inputValue = String(value);
  if (!inputValue.includes('.')) return inputValue;

  return inputValue.replace(/0+$/, '').replace(/\.$/, '');
};

export const formatEstimateQuantity = (value, locale = 'ja-JP', decimalPlaces = null) => {
  if (value === '' || value == null) return '';

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';

  const preservedDecimalPlaces = Number(decimalPlaces);
  const hasPreservedDecimalPlaces = decimalPlaces !== null &&
    decimalPlaces !== undefined && decimalPlaces !== '' &&
    Number.isInteger(preservedDecimalPlaces) &&
    preservedDecimalPlaces >= 0 && preservedDecimalPlaces <= 2;
  return numericValue.toLocaleString(locale, {
    minimumFractionDigits: hasPreservedDecimalPlaces ? preservedDecimalPlaces : 0,
    maximumFractionDigits: hasPreservedDecimalPlaces ? preservedDecimalPlaces : 2
  });
};

// 縦型は摘要1欄のみ入力するため、部位・仕様・備考を改行でつないで仕様へまとめる。
// PDFの摘要欄も同じ順で表示しているため、見た目は変わらない。
const PORTRAIT_REMARKS_FIELDS = [['part_nm', 'specification', 'notes']];
export const mergePortraitRemarks = line => {
  if (!line || ['note', 'subtotal'].includes(line.line_type)) return line;
  PORTRAIT_REMARKS_FIELDS.forEach(([partField, specificationField, notesField]) => {
    if (!line[partField] && !line[notesField]) return;
    line[specificationField] = [line[partField], line[specificationField], line[notesField]]
      .map(value => String(value ?? '').trim())
      .filter(Boolean)
      .join('\n');
    line[partField] = '';
    line[notesField] = '';
  });
  return line;
};
export const mergeEstimatePortraitRemarks = estimate => {
  collectEstimateLines(estimate).forEach(mergePortraitRemarks);
  return estimate;
};

export const calculateLineAmount = line => {
  if (
    ['detail', 'discount'].includes(line?.line_type) &&
    !line?.manual_amount_flg &&
    line?.quantity !== '' &&
    line?.quantity != null &&
    line?.unit_price !== '' &&
    line?.unit_price != null
  ) {
    return Math.round(Number(line.quantity) * Number(line.unit_price));
  }

  return Math.round(Number(line?.amount || 0));
};

// 値引は金額だけを入力する。数量×単価で登録済みの値引は、計算結果の金額へ置き換える。
export const toAmountOnlyDiscount = line => {
  if (line?.line_type !== 'discount') return line;
  line.amount = calculateLineAmount(line);
  line.quantity = '';
  line.quantity_decimal_places = null;
  line.unit_price = '';
  line.unit_nm = '';
  line.manual_amount_flg = false;
  line.manual_amount_reason = '';
  return line;
};

export const isAutoCalculatedLine = line =>
  ['detail', 'discount'].includes(line?.line_type) &&
  !line?.manual_amount_flg &&
  line?.quantity !== '' &&
  line?.quantity != null &&
  line?.unit_price !== '' &&
  line?.unit_price != null;

export const collectEstimateLines = estimate => {
  const sectionLines = visibleRecords(estimate?.sections).flatMap(section =>
    visibleRecords(section.lines)
  );
  return [...sectionLines, ...visibleRecords(estimate?.unsectioned_lines)];
};

const roundTax = (amount, roundingType) => {
  if (roundingType === 'ceil') return Math.ceil(amount);
  if (roundingType === 'round') return Math.round(amount);
  return Math.floor(amount);
};

export const calculateEstimatePreview = estimate => {
  const lines = collectEstimateLines(estimate);
  const details = lines.filter(line => line.line_type === 'detail');
  const discounts = lines.filter(line => line.line_type === 'discount');
  const subtotal = details.reduce((sum, line) => sum + calculateLineAmount(line), 0);
  const discount = discounts.reduce((sum, line) => sum + calculateLineAmount(line), 0);
  const taxableDetails = details
    .filter(line => line.tax_type === 'taxable')
    .reduce((sum, line) => sum + calculateLineAmount(line), 0);
  const taxableDiscounts = discounts
    .filter(line => line.tax_type === 'taxable')
    .reduce((sum, line) => sum + calculateLineAmount(line), 0);
  const taxable = Math.max(taxableDetails - taxableDiscounts, 0);
  const tax = roundTax(
    taxable * Number(estimate?.tax_rate || 0) / 100,
    estimate?.tax_rounding_type
  );

  return {
    subtotal,
    discount,
    taxable,
    tax,
    net: Math.max(subtotal - discount, 0),
    total: Math.max(subtotal - discount, 0) + tax
  };
};

export const calculateSectionAmounts = section => {
  const lines = visibleRecords(section?.lines);
  const detail = lines
    .filter(line => line.line_type === 'detail')
    .reduce((sum, line) => sum + calculateLineAmount(line), 0);
  const discount = lines
    .filter(line => line.line_type === 'discount')
    .reduce((sum, line) => sum + calculateLineAmount(line), 0);

  return {
    detail,
    discount,
    net: detail - discount
  };
};

export const chunkRows = (rows, rowsPerPage) => {
  const capacity = Math.max(Number(rowsPerPage) || 1, 1);
  const source = [...(rows || [])];
  if (source.length === 0) return [[]];

  const pages = [];
  for (let index = 0; index < source.length; index += capacity) {
    pages.push(source.slice(index, index + capacity));
  }
  return pages;
};

export const paginateRowsWithFinalReserve = (rows, rowsPerPage, finalReservedRows = 0) => {
  const capacity = Math.max(Number(rowsPerPage) || 1, 1);
  const reserve = Math.min(Math.max(Number(finalReservedRows) || 0, 0), capacity - 1);
  const finalCapacity = capacity - reserve;
  const source = [...(rows || [])];
  const pages = [];
  let index = 0;

  while (source.length - index > finalCapacity) {
    pages.push(source.slice(index, index + capacity));
    index += capacity;
  }
  pages.push(source.slice(index));
  return pages;
};

export const rowsDisplayWeight = (rows, rowWeight = () => 1) =>
  (rows || []).reduce((total, row) => total + Math.max(Number(rowWeight(row)) || 1, 1), 0);

export const fillRowsToCapacity = (
  rows,
  rowsPerPage,
  reservedRows = 0,
  rowWeight = () => 1,
  keyPrefix = 'blank'
) => {
  const capacity = Math.max(Number(rowsPerPage) || 1, 1);
  const reserve = Math.max(Number(reservedRows) || 0, 0);
  const source = [...(rows || [])];
  const blankCount = Math.max(
    Math.floor(capacity - reserve - rowsDisplayWeight(source, rowWeight)),
    0
  );
  const blanks = Array.from({ length: blankCount }, (_, index) => ({
    key: `${keyPrefix}-${index}`,
    kind: 'blank'
  }));
  return [...source, ...blanks];
};

const visualTextWidth = value =>
  [...String(value ?? '')].reduce((width, character) =>
    width + (/^[\x20-\x7e\uff61-\uff9f]$/.test(character) ? 0.5 : 1), 0);

export const wrappedTextLineCount = (value, columnsPerLine) => {
  const capacity = Math.max(Number(columnsPerLine) || 1, 1);
  return String(value ?? '')
    .split(/\r?\n/)
    .reduce((lines, text) => lines + Math.max(Math.ceil(visualTextWidth(text) / capacity), 1), 0);
};

export const paginateRowsByWeight = (
  rows,
  rowsPerPage,
  finalReservedRows = 0,
  rowWeight = () => 1,
  options = {}
) => {
  const capacity = Math.max(Number(rowsPerPage) || 1, 1);
  const reserve = Math.min(Math.max(Number(finalReservedRows) || 0, 0), capacity - 1);
  const finalCapacity = capacity - reserve;
  const source = [...(rows || [])];
  const keepWithNext = typeof options.keepWithNext === 'function'
    ? options.keepWithNext
    : () => false;
  if (source.length === 0) return [[]];

  const pages = [];
  let index = 0;
  while (rowsDisplayWeight(source.slice(index), rowWeight) > finalCapacity) {
    const page = [];
    let pageWeight = 0;
    while (index < source.length) {
      const nextWeight = Math.max(Number(rowWeight(source[index])) || 1, 1);
      const followingWeight = index + 1 < source.length && keepWithNext(source[index], source[index + 1])
        ? Math.max(Number(rowWeight(source[index + 1])) || 1, 1)
        : 0;
      if (page.length && pageWeight + nextWeight + followingWeight > capacity) break;
      if (page.length && pageWeight + nextWeight > capacity) break;
      page.push(source[index]);
      pageWeight += nextWeight;
      index += 1;
      if (pageWeight >= capacity) break;
    }
    pages.push(page);
  }
  pages.push(source.slice(index));
  return pages;
};

export const buildPortraitRows = estimate => {
  const rows = [];
  visibleRecords(estimate?.sections).forEach(section => {
    visibleRecords(section.lines).forEach(line => {
      rows.push({
        key: `line-${line.local_key || line.id}`,
        kind: 'line',
        section,
        sectionIndex: estimate.sections.indexOf(section),
        line,
        lineIndex: section.lines.indexOf(line)
      });
    });
  });
  visibleRecords(estimate?.unsectioned_lines).forEach(line => {
    rows.push({
      key: `unsectioned-${line.local_key || line.id}`,
      kind: 'line',
      section: null,
      sectionIndex: -1,
      line,
      lineIndex: estimate.unsectioned_lines.indexOf(line)
    });
  });
  return rows;
};

export const buildLandscapeDetailRows = estimate => {
  const rows = [];
  visibleRecords(estimate?.sections).forEach((section, sectionIndex) => {
    const rawSectionIndex = estimate.sections.indexOf(section);
    rows.push({
      key: `detail-section-${section.local_key || section.id || sectionIndex}`,
      kind: 'section',
      section,
      sectionIndex: rawSectionIndex,
      displayIndex: sectionIndex
    });
    visibleRecords(section.lines).forEach((line, lineIndex) => {
      rows.push({
        key: `detail-line-${line.local_key || line.id}`,
        kind: 'line',
        section,
        sectionIndex: rawSectionIndex,
        line,
        lineIndex: section.lines.indexOf(line),
        displayIndex: lineIndex
      });
    });
    rows.push({
      key: `detail-subtotal-${section.local_key || section.id || sectionIndex}`,
      kind: 'subtotal',
      section,
      sectionIndex: rawSectionIndex,
      displayIndex: sectionIndex,
      amount: calculateSectionAmounts(section).net
    });
  });

  const unsectioned = visibleRecords(estimate?.unsectioned_lines);
  if (unsectioned.length) {
    rows.push({ key: 'detail-unsectioned-heading', kind: 'unsectioned' });
    unsectioned.forEach((line, lineIndex) => {
      rows.push({
        key: `detail-unsectioned-${line.local_key || line.id}`,
        kind: 'line',
        section: null,
        sectionIndex: -1,
        line,
        lineIndex: estimate.unsectioned_lines.indexOf(line),
        displayIndex: lineIndex
      });
    });
  }
  return rows;
};

export const buildLandscapeBreakdownRows = estimate => {
  const rows = [];
  visibleRecords(estimate?.sections).forEach((section, sectionIndex) => {
    rows.push({
      key: `breakdown-section-${section.local_key || section.id || sectionIndex}`,
      kind: 'section',
      section,
      displayIndex: sectionIndex,
      amount: calculateSectionAmounts(section).detail
    });
    visibleRecords(section.lines).forEach((line, lineIndex) => {
      if (line.line_type === 'discount') {
        rows.push({
          key: `breakdown-discount-${line.local_key || line.id || lineIndex}`,
          kind: 'discount',
          line,
          amount: calculateLineAmount(line)
        });
      } else if (line.line_type === 'note') {
        rows.push({
          key: `breakdown-note-${line.local_key || line.id || lineIndex}`,
          kind: 'note',
          line
        });
      }
    });
  });

  visibleRecords(estimate?.unsectioned_lines).forEach((line, lineIndex) => {
    if (line.line_type === 'subtotal') return;
    rows.push({
      key: `breakdown-unsectioned-${line.local_key || line.id || lineIndex}`,
      kind: line.line_type === 'note'
        ? 'note'
        : line.line_type === 'discount'
          ? 'discount'
          : 'unsectioned',
      line,
      amount: calculateLineAmount(line)
    });
  });

  return rows;
};
